<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('landlord.shops.title') }}
          </h1>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">
        <div class="card card-flush">
          <!-- Header with search -->
          <div class="card-header border-0 pt-6">
            <div class="card-title">
              <div class="d-flex align-items-center position-relative my-1">
                <i class="fas fa-search position-absolute ms-4 fs-6"></i>
                <input type="text" v-model="search" @input="onSearch"
                       class="form-control form-control-solid w-250px ps-12"
                       :placeholder="$t('landlord.shops.search_placeholder')" />
              </div>
            </div>
            <div class="card-toolbar">
              <router-link to="/landlord/shops/create" class="btn btn-sm btn-primary">
                <i class="fas fa-plus me-1"></i> {{ $t('landlord.shops.create_new') }}
              </router-link>
            </div>
          </div>

          <!-- Table -->
          <div class="card-body py-4">
            <div v-if="loading" class="text-center py-10">
              <span class="spinner-border text-primary"></span>
            </div>
            <div v-else-if="!shops.length" class="text-center py-10 text-gray-500">
              {{ $t('landlord.shops.no_results') }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr class="fw-bolder text-muted">
                    <th>{{ $t('landlord.shops.col_name') }}</th>
                    <th>{{ $t('landlord.shops.col_phone') }}</th>
                    <th>{{ $t('landlord.shops.col_domain') }}</th>
                    <th>{{ $t('landlord.shops.col_status') }}</th>
                    <th>{{ $t('landlord.shops.col_date') }}</th>
                    <th class="text-end">{{ $t('landlord.shops.col_actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="shop in shops" :key="shop.id">
                    <td>
                      <router-link :to="`/landlord/shops/${shop.id}`" class="text-dark fw-bold text-hover-primary d-flex align-items-center gap-3">
                        <img v-if="shop.logo" :src="shop.logo" class="rounded" style="width:32px;height:32px;object-fit:cover;" />
                        <div v-else class="rounded bg-light-primary d-flex align-items-center justify-content-center" style="width:32px;height:32px;">
                          <i class="fas fa-store fs-7 text-primary"></i>
                        </div>
                        {{ shop.name || shop.id }}
                      </router-link>
                    </td>
                    <td>{{ shop.phone ? [shop.phone_code, shop.phone].filter(Boolean).join(' ') : '-' }}</td>
                    <td>
                      <span v-if="shop.domain" class="badge badge-light-primary">{{ shop.domain }}</span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td>
                      <template v-if="shop.pending_validation">
                        <!-- la validation se fait dans le détail (onglet Validation) -->
                        <router-link :to="`/landlord/shops/${shop.id}`" class="badge badge-light-warning">{{ $t('landlord.shops.pending_' + shop.pending_validation) }}</router-link>
                        <div v-if="shop.pending_plan" class="text-gray-600 fs-7 mt-1">{{ shop.pending_plan }}</div>
                      </template>
                      <span v-else class="badge" :class="shop.online ? 'badge-light-success' : 'badge-light-danger'">
                        {{ shop.online ? $t('landlord.shops.active') : $t('landlord.shops.inactive') }}
                      </span>
                    </td>
                    <td class="text-gray-500">{{ formatDate(shop.created_at) }}</td>
                    <td class="text-end">
                      <router-link :to="`/landlord/shops/${shop.id}`" class="btn btn-icon btn-sm btn-light-primary me-1" :title="$t('landlord.shops.view')">
                        <i class="fas fa-eye"></i>
                      </router-link>
                      <button @click="onToggleStatus(shop)" class="btn btn-icon btn-sm me-1"
                              :class="shop.online ? 'btn-light-warning' : 'btn-light-success'"
                              :title="shop.online ? $t('landlord.shops.deactivate') : $t('landlord.shops.activate')">
                        <i :class="shop.online ? 'fas fa-ban' : 'fas fa-check'"></i>
                      </button>
                      <button @click="onDelete(shop)" class="btn btn-icon btn-sm btn-light-danger" :title="$t('landlord.shops.delete')">
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="meta && meta.last_page > 1" class="d-flex justify-content-between align-items-center mt-5">
              <div class="text-gray-500 fs-7">
                {{ $t('landlord.shops.showing') }} {{ meta.from }} - {{ meta.to }} / {{ meta.total }}
              </div>
              <ul class="pagination">
                <li class="page-item" :class="{ disabled: meta.current_page <= 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(meta.current_page - 1)">
                    <i class="fas fa-chevron-left fs-7"></i>
                  </a>
                </li>
                <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === meta.current_page }">
                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: meta.current_page >= meta.last_page }">
                  <a class="page-link" href="#" @click.prevent="goToPage(meta.current_page + 1)">
                    <i class="fas fa-chevron-right fs-7"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'LandlordShopsIndex',
    data() {
        return {
            search: '',
            loading: false,
            searchTimeout: null,
            currentPage: 1,
        }
    },
    computed: {
        ...mapGetters('landlord', { shops: 'shops', meta: 'shopsMeta' }),

        visiblePages() {
            if (!this.meta) return []
            const pages = []
            const start = Math.max(1, this.meta.current_page - 2)
            const end = Math.min(this.meta.last_page, this.meta.current_page + 2)
            for (let i = start; i <= end; i++) {
                pages.push(i)
            }
            return pages
        }
    },
    mounted() {
        this.loadShops()
    },
    methods: {
        ...mapActions('landlord', ['fetchShops', 'toggleShopStatus', 'deleteShop']),

        loadShops() {
            this.loading = true
            this.fetchShops({ page: this.currentPage, search: this.search, per_page: 15 })
                .finally(() => { this.loading = false })
        },

        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.loadShops()
            }, 400)
        },

        goToPage(page) {
            if (page < 1 || (this.meta && page > this.meta.last_page)) return
            this.currentPage = page
            this.loadShops()
        },

        onToggleStatus(shop) {
            const action = shop.online ? this.$t('landlord.shops.deactivate') : this.$t('landlord.shops.activate')
            Swal.fire({
                text: `${action} "${shop.name || shop.id}" ?`,
                icon: 'warning',
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: i18n.global.t('swal.yes'),
                cancelButtonText: i18n.global.t('swal.no'),
                customClass: {
                    confirmButton: 'btn fw-bold btn-primary',
                    cancelButton: 'btn fw-bold btn-secondary'
                }
            }).then((result) => {
                if (result.value) {
                    this.toggleShopStatus(shop.id).then(() => {
                        window.toastr['success'](i18n.global.t('landlord.shops.status_updated'))
                        this.loadShops()
                    })
                }
            })
        },

        onDelete(shop) {
            Swal.fire({
                text: this.$t('landlord.shops.confirm_delete', { name: shop.name || shop.id }),
                icon: 'error',
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: i18n.global.t('swal.yes'),
                cancelButtonText: i18n.global.t('swal.no'),
                customClass: {
                    confirmButton: 'btn fw-bold btn-danger',
                    cancelButton: 'btn fw-bold btn-secondary'
                }
            }).then((result) => {
                if (result.value) {
                    this.deleteShop(shop.id).then(() => {
                        window.toastr['success'](i18n.global.t('landlord.shops.deleted'))
                        this.loadShops()
                    })
                }
            })
        },

        formatDate(dateStr) {
            if (!dateStr) return '-'
            const d = new Date(dateStr)
            return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        }
    }
}
</script>
